import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
} from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { getFlatCategoriesList, ICategory } from './constants';
import { IconCross } from '../../../utils/icons';
import { Container } from './Container';

interface Props {
  categoriesList: ICategory[];
  currentCategoryId?: number | null;
  parentId: number | null;
  setParentId: (id: number | null) => void;
}

export const ParentSelector: FC<Props> = ({
  categoriesList,
  currentCategoryId,
  parentId,
  setParentId,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(parentId);

  useEffect(() => {
    setSelectedId(parentId);
  }, [parentId]);

  const onCancel = () => {
    setOpen(false);
    setSelectedId(parentId);
  };

  const parentCategory = useMemo(
    () =>
      getFlatCategoriesList(categoriesList).find((cat) => cat.id === parentId),
    [parentId, categoriesList]
  );

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outlined"
        sx={{ width: '100%', minWidth: 0, px: 2 }}
      >
        <Typography
          variant="body2"
          textTransform="capitalize"
          sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            textAlign: 'center',
          }}
        >
          {parentCategory
            ? `Selected Parent: ${parentCategory.name} `
            : 'Select Category Parent'}
        </Typography>
      </Button>
      {open && (
        <Dialog
          fullWidth
          maxWidth="md"
          open
          onClose={onCancel}
          sx={{
            '& .MuiDialog-paper': {
              '@media (max-width: 599.95px)': {
                m: 1.5,
                width: 'calc(100% - 24px)',
                maxHeight: 'calc(100% - 24px)',
              },
            },
          }}
        >
          <DialogTitle
            className="!border-b !border-border-token"
            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}
          >
            <Box className="flex items-center justify-between">
              <Typography
                fontFamily="CircularStdBlack"
                fontWeight={900}
                sx={{ fontSize: { xs: 18, sm: 24 } }}
              >
                Select Parent Category
              </Typography>
              <Box className="p-2 cursor-pointer" onClick={onCancel}>
                <IconCross />
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 }, mt: { xs: 1, sm: 2 } }}>
            <Container
              isMinimal
              data={categoriesList}
              selector={{
                currentCategoryId,
                selectedId,
                onSelect: setSelectedId,
              }}
            />
          </DialogContent>
          <DialogActions
            className="border-t border-border-token"
            sx={{ p: { xs: 1.5, sm: 2 } }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: { xs: 1.5, sm: 3 },
              }}
            >
              <Button
                size="large"
                variant="outlined"
                onClick={onCancel}
                sx={{ flex: { xs: 1, sm: 'initial' } }}
              >
                Cancel
              </Button>
              <Button
                size="large"
                variant="contained"
                onClick={() => {
                  setParentId(selectedId);
                  setOpen(false);
                }}
                sx={{ flex: { xs: 1, sm: 'initial' } }}
              >
                Submit
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
